import { useSearchParams } from 'next/navigation';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { UsePagingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UsePagingFragmentFragmentDoc';
import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import {
  decodePage,
  decodePageSize,
  decodePagingMode,
  encodePage,
  encodePagingMode,
} from '../queryEncoding';
import { useDefaultPageSize } from './useDefaultPageSize';
import { getTestId } from '../utils/featureFlags/getTestId';
import { fewerProductsPerPage } from '../activeFeatureFlags';

/* GraphQL */ `
  fragment UsePagingFragment on SearchProductResult {
    summary {
      totalResultCount
    }
  }
`;

const DEFAULT_PAGE = 1;

/**
 * Use page option persisted in the url query
 *
 * Changing pages results in a router.push, which will trigger a data reload
 */
export const usePaging = (maskedData: FragmentType<typeof UsePagingFragmentFragmentDoc>) => {
  const data = unmask<typeof UsePagingFragmentFragmentDoc>(maskedData);

  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const defaultPageSize = useDefaultPageSize();
  const {
    search: { paginationMode },
  } = useConfig();

  const { setOutcome } = useAbTesting();

  const query = searchParamsToObject(searchParams);
  const pageSize = decodePageSize(query) ?? defaultPageSize;
  const currentPage = decodePage(query) ?? DEFAULT_PAGE;
  const pagingModeFromQuery = decodePagingMode(query);

  const pagingMode = paginationMode === 'loadMore' ? 'infinite' : pagingModeFromQuery;
  const pageCount = Math.ceil(data.summary.totalResultCount / pageSize);

  const pushPageChange = (newPage: number, keepScrollPosition?: boolean) => {
    if (newPage === currentPage) return;

    const partialQuery = encodePage(newPage === DEFAULT_PAGE ? undefined : newPage);

    pushQueryChange(partialQuery, {
      scroll: !keepScrollPosition,
    });

    setOutcome(getTestId(fewerProductsPerPage), { LOAD: newPage });
  };

  const pushPagingMode = (newPagingMode: 'pagination' | 'infinite') => {
    // new infinite scrolling only supports infinite mode, no pagination
    if (paginationMode === 'loadMore') return;
    // if mode is already correct, do nothing
    if (newPagingMode === pagingMode) return;

    const partialQuery = {
      // reset page to the default when switching modes
      ...encodePage(undefined),
      ...encodePagingMode(newPagingMode),
    };

    pushQueryChange(partialQuery);
  };

  return {
    pageSize,
    pageCount,
    currentPage,
    pagingMode,
    pushPageChange,
    pushPagingMode,
  };
};
